import axios from 'axios'
import { Loading } from 'element-ui'
import requestErr from './request-err.js'
import Cookies from 'js-cookie'
import { message, Modal } from 'ant-design-vue'
import store from '@/store'
import { toLogin } from '@/utils/common'

// 模块全局局变量，标记是否正在刷新token
let isRefreshing = false
// 模块全局变量，重试队列，每一项将是一个待执行的函数形式
let requestArr = []
// 模块全局变量，标识用户信息已经失效，但是用户没有点击弹窗时，不再重复验证登录状态（项目里面有轮询）
let isLogout = false

const service = axios.create({
  // baseURL: process.env.VUE_APP_BASE_API,
  withCredentials: true
})

// 请求拦截器
service.interceptors.request.use(
  (config) => {
    const xsrf = Cookies.get('XSRF-TOKEN')
    if (xsrf) {
      config.headers['X-XSRF-TOKEN'] = xsrf
    }

    const ac = Cookies.get('X-JCZH-AC')
    if (ac && ac !== 'undefined' && ac !== 'null') {
      config.headers['X-PURCHASE-AC'] = ac
      config.headers['X-JCZH-AC'] = ac
    }

    config.headers['Accept-Language'] =
      'zh-CN,zh;q=0.9,en-US;q=0.7,en;q=0.8,zh-TW;q=0.6'

    let url = config.url
    // get参数编码
    if (config.method === 'get' && config.params) {
      url += '?'
      const keys = Object.keys(config.params)
      for (const key of keys) {
        const tempData =
          config.params[key] === '' ||
          config.params[key] === undefined ||
          config.params[key] == null
            ? ''
            : config.params[key]
        url += `${key}=${encodeURIComponent(tempData)}&`
      }
      url = url.substring(0, url.length - 1)
      config.params = {}
    }
    config.url = url
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// 响应拦截器
service.interceptors.response.use(
  // http请求成功的响应拦截
  (response) => {
    const res = response.data

    // 如果是响应类型是blob，则直接返回
    if (
      response.config.responseType === 'blob' ||
      response.config.responseType === 'arraybuffer' ||
      response.config.responseType === 'stream'
    ) {
      return res
    }

    if (res.success) {
      store.commit('authFile/ADD_AUTH_FILES_BY_API', res.data)
      return res
    }

    if (!res.message && typeof message === 'object') {
      res.message = requestErr[res.code] || '数据请求错误'
    }
    response.message = res.message || requestErr[res.code] || '数据请求错误'
    return Promise.reject(response)
  },

  // http请求失败的响应拦截
  (error) => {
    const { code, message, response = {} } = error
    const status = response.status
    const config = response.config

    if (status == 401 && config.url.indexOf('/passport/status') === -1) {
      if (isRefreshing) {
        return new Promise((resolve) => {
          requestArr.push(() => {
            resolve(service(config))
          })
        })
      }

      if (isLogout) return Promise.reject()
      isRefreshing = true
      return store
        .dispatch('user/checkLogin')
        .then((res) => {
          isRefreshing = false
          requestArr.forEach((cb) => cb())
          requestArr = []
          return service(config)
        })
        .catch(() => {
          Modal.confirm({
            title: '用户信息已过期，请重新登录',
            icon: 'exclamation-circle',
            onOk: () => {
              toLogin()
            },
            onCancel: () => {}
          })

          isRefreshing = false
          isLogout = true
          return Promise.reject()
        })
    }

    if (response.data && response.data instanceof Object) {
      if (!response.data.message) {
        response.data.message = requestErr[status] || '数据请求错误'
      }
    } else {
      response.data = {
        code: response.status,
        message: requestErr[status] || '数据请求错误'
      }
    }

    if (code === 'ECONNABORTED' || message === 'Network Error') {
      response.data.message = '网络错误'
    }

    return Promise.reject(response)
  }
)

/**
 * 将向后台请求的数据，进行操作；
 * 过滤空字符串的数据；
 * 如果isFormData为true，则将数据转换成FormData类型
 * @param {Object} data 需要进行操作的数据
 * @param {Boolean} isFormData 是否使用form表单的形式提交数据
 */
function changeDataToFormData(data, isFormData) {
  const formData = new FormData()
  const object = Object.create(null)

  if (Object.prototype.toString.call(data) !== '[object Object]') {
    return data
  }

  if (isFormData) {
    Object.keys(data).forEach((key) => {
      if (data[key] !== '') {
        formData.append(key, data[key])
      }
    })
    return formData
  } else {
    Object.keys(data).forEach((key) => {
      if (data[key] !== '') {
        object[key] = data[key]
      }
    })
    return object
  }
}

// 模块全局变量，loading计数器，实现多个接口请求，最后一个接口返回后才loading消失
let loadingNum = 0
let loadingInstance = null

/**
 * 对定义的axios进行再一次的封装，实现表单数据形式请求，loading和请求错误提示
 *
 * @param {Object} ajax  接口请求的配置参数
 * @param {Object} option  额外配置
 * @returns {Promise<void>}   返回promise的axios实例
 */
export default function (
  ajax,
  option = { isFormData: false, needLoading: true }
) {
  ajax.data &&
    (ajax.data = changeDataToFormData(ajax.data, option?.isFormData || false))
  ajax.params &&
    (ajax.params = changeDataToFormData(
      ajax.params,
      option?.isFormData || false
    ))

  // 需要全局loading时的计数
  if (option?.needLoading) {
    loadingNum++

    loadingInstance = Loading.service({
      fullscreen: true,
      body: true,
      customClass: 'z-index-loading',
      text: '正在加载，请稍后'
    })
  }

  return service(ajax).finally(() => {
    // loading消失的处理
    if (option?.needLoading) {
      loadingNum--
      setTimeout(() => {
        if (loadingNum <= 0 && loadingInstance) loadingInstance.close()
      }, 0)
    }
  })
}
